import React from "react";
import PropTypes from "prop-types";
import { useId } from "Hooks";
import classes from "./StepsList.scss";
import classNames from "classnames";

/**
 * StepsList Component
 * A container for rendering a list of steps in a structured format.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Allowed compound components (Step, StepHeader, StepDescription, StepCustomComponent)
 * @example
 * <StepsList>
 *   <StepsList.Step stepNumber={1}>
 *     <StepsList.StepHeader value="Step 1" />
 *     <StepsList.StepDescription value="Description for Step 1" />
 *   </StepsList.Step>
 * </StepsList>
 */
const StepsList = ({ children }) => {
  return <div className={classes.container}>{children}</div>;
};

StepsList.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Step Component
 * Represents a single step in the StepsList.
 *
 * @param {object} props - Component props
 * @param {number} props.stepNumber - The step number to display
 * @param {React.ReactNode} props.children - Content of the step
 * @example
 * <StepsList.Step stepNumber={1}>
 *   <StepsList.StepHeader value="Step 1" />
 *   <StepsList.StepDescription value="Description for Step 1" />
 * </StepsList.Step>
 */
const Step = ({ stepNumber, children, stepDetailsWrapperClassName }) => {
  const stepId = useId();

  return (
    <div
      className={classes.step}
      role="group"
      aria-labelledby={`${stepId}-header`}
      aria-describedby={`${stepId}-description`}
    >
      <div className={classes.stepNumberWrapper}>
        <div
          className={classNames(classes.stepNumber, "text-body-s")}
          aria-hidden="true"
        >
          {stepNumber}
        </div>
      </div>
      <div
        className={classNames(
          classes.stepDetailsWrapper,
          stepDetailsWrapperClassName
        )}
      >
        {React.Children.map(children, child =>
          React.cloneElement(child, {
            stepId,
          })
        )}
      </div>
    </div>
  );
};

StepsList.Step = Step;
StepsList.Step.displayName = "StepsList.Step";
StepsList.Step.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  stepGap: PropTypes.string,
};

/**
 * StepHeader Component
 * Displays the header/title of a step.
 *
 * @param {object} props - Component props
 * @param {string} props.value - The text value of the step header
 * @example
 * <StepsList.StepHeader value="Step 1" />
 */
const StepHeader = ({ value, stepId }) => {
  return (
    <div
      className={classNames(classes.stepHeader, "text-label")}
      role="heading"
      aria-level="2"
      id={`${stepId}-header`}
    >
      {value}
    </div>
  );
};

StepsList.StepHeader = StepHeader;
StepsList.StepHeader.displayName = "StepsList.StepHeader";
StepsList.StepHeader.propTypes = {
  value: PropTypes.string.isRequired,
};

/**
 * StepDescription Component
 * Displays the description of a step.
 *
 * @param {object} props - Component props
 * @param {string} props.value - The text value of the step description
 * @example
 * <StepsList.StepDescription value="Description for Step 1" />
 */
const StepDescription = ({ value, stepId, className }) => {
  return (
    <div
      className={classNames(classes.stepDescription, "text-body-s", className)}
      id={`${stepId}-description`}
    >
      {value}
    </div>
  );
};

StepsList.StepDescription = StepDescription;
StepsList.StepDescription.displayName = "StepsList.StepDescription";
StepsList.StepDescription.propTypes = {
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
};

/**
 * StepCustomComponent Component
 * Allows for custom components to be rendered within a step.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Custom React components or elements to render
 * @example
 * <StepsList.StepCustomComponent>
 *   <CustomComponent />
 * </StepsList.StepCustomComponent>
 */
const StepCustomComponent = ({ children, className }) => {
  return (
    <div className={classNames(classes.customComponent, className)}>
      {children}
    </div>
  );
};

StepsList.StepCustomComponent = StepCustomComponent;
StepsList.StepCustomComponent.displayName = "StepsList.StepCustomComponent";
StepsList.StepCustomComponent.propTypes = {
  children: PropTypes.node.isRequired,
};

export default StepsList;
